import type { DurationParts, TimeInput, TimeRemaining } from './types';
import { toEpochMs } from './isomorphic/internal/coerce';

const ZERO: DurationParts = { days: 0, hours: 0, minutes: 0, seconds: 0 };

/**
 * Break a duration in milliseconds into whole `{ days, hours, minutes, seconds }`.
 * Negative input floors at zero. Pure — the countdown hook (PR3) feeds it the
 * `performance.now()`-anchored ms-remaining each tick.
 *
 * @sideEffect none — pure.
 */
export function breakDuration(ms: number): DurationParts {
    const t = Math.max(0, ms);
    return {
        days: Math.floor(t / 86400000),
        hours: Math.floor(t / 3600000) % 24,
        minutes: Math.floor(t / 60000) % 60,
        seconds: Math.floor(t / 1000) % 60,
    };
}

/**
 * Compute the countdown state for an event window against a caller-supplied
 * `now` (epoch ms). `now` is a parameter — never `Date.now()` — so a
 * server-anchored clock can be passed in (the honest-countdown fix) and the
 * function stays pure and testable.
 *
 * - before `start` (when given) → `upcoming`, counting down to the start
 * - between `start` and `end`   → `running`, counting down to the close
 * - at/after `end`              → `ended`, all parts zero
 *
 * When `pausedAt` is set the countdown FREEZES: remaining time is computed
 * against that fixed instant instead of `now`, so every surface's "time left"
 * holds steady at `end - pausedAt` for the duration of a pause (BUG-1166). Pass
 * the auction's `paused_at` while a timed auction is paused; pass null/undefined
 * for a live countdown. On resume the caller clears `pausedAt` and `end` has been
 * pushed out by the elapsed pause, so the countdown continues from the exact held
 * value.
 *
 * @sideEffect none — pure.
 */
export function timeRemaining({
    start,
    end,
    now,
    pausedAt,
}: {
    start?: TimeInput | null;
    end: TimeInput;
    now: number;
    pausedAt?: TimeInput | null;
}): TimeRemaining {
    const endMs = toEpochMs(end);
    if (endMs == null) return { totalMs: 0, ...ZERO, status: 'ended' };

    // While paused, anchor "now" to the pause instant so the countdown holds.
    const pausedMs = pausedAt != null ? toEpochMs(pausedAt) : null;
    const effectiveNow = pausedMs ?? now;

    const startMs = start != null ? toEpochMs(start) : null;
    if (startMs != null && effectiveNow < startMs) {
        const diff = startMs - effectiveNow;
        return { totalMs: diff, ...breakDuration(diff), status: 'upcoming' };
    }

    if (effectiveNow <= endMs) {
        const diff = endMs - effectiveNow;
        return { totalMs: diff, ...breakDuration(diff), status: 'running' };
    }

    return { totalMs: 0, ...ZERO, status: 'ended' };
}
